'use client';

import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';

interface TrackActivityParams {
  activityType: 'read' | 'saved' | 'used_tool' | 'downloaded' | 'shared';
  resourceType?: string;
  resourceId?: string;
  metadata?: Record<string, any>;
}

/**
 * Hook for tracking user activity
 * Automatically tracks page views and provides manual tracking methods
 */
export function useActivityTracking() {
  const { isAuthenticated } = useAuth();
  const trackedRef = useRef(false);

  const trackActivity = useCallback(async ({
    activityType,
    resourceType,
    resourceId,
    metadata = {}
  }: TrackActivityParams) => {
    if (!isAuthenticated) return;

    try {
      await fetch('/api/secure/activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          activity_type: activityType,
          resource_type: resourceType,
          resource_id: resourceId,
          metadata
        })
      });
    } catch (error) {
      // Silently fail - activity tracking shouldn't break UX
      console.error('Failed to track activity:', error);
    }
  }, [isAuthenticated]);

  return { trackActivity };
}

/**
 * Hook for tracking page views
 * Call this in page components to automatically track when users view content
 */
export function usePageViewTracking(
  contentType: string,
  contentSlug: string,
  contentTitle?: string
) {
  const { isAuthenticated } = useAuth();
  const { trackActivity } = useActivityTracking();
  const trackedRef = useRef(false);

  useEffect(() => {
    // Only track once per page load
    if (trackedRef.current || !isAuthenticated) return;
    trackedRef.current = true;

    trackActivity({
      activityType: 'read',
      resourceType: contentType,
      resourceId: contentSlug,
      metadata: { title: contentTitle }
    });
  }, [isAuthenticated, contentType, contentSlug, contentTitle, trackActivity]);
}

/**
 * Hook for tracking reading progress
 * Tracks scroll depth and time spent on page
 */
export function useReadingProgress(
  contentType: string,
  contentSlug: string,
  onProgressUpdate?: (progress: number) => void
) {
  const { isAuthenticated } = useAuth();
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const startTimeRef = useRef<number>(0);
  const lastSaveRef = useRef<number>(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    startTimeRef.current = Date.now();

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);

      // Update progress if increased by at least 10%
      if (scrollPercent > progressRef.current + 10) {
        progressRef.current = scrollPercent;
        setProgress(scrollPercent);
        onProgressUpdate?.(scrollPercent);

        // Save progress every 30 seconds
        const now = Date.now();
        if (now - lastSaveRef.current > 30000) {
          lastSaveRef.current = now;
          const timeSpent = Math.round((now - startTimeRef.current) / 1000);

          fetch('/api/secure/activity', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              activity_type: 'read',
              resource_type: contentType,
              resource_id: contentSlug,
              metadata: {
                scroll_percent: scrollPercent,
                time_spent: timeSpent,
                progress_update: true
              }
            })
          }).catch(() => {}); // Silently fail
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated, contentType, contentSlug, onProgressUpdate]);

  return { progress };
}

/**
 * Hook for tracking downloads
 */
export function useDownloadTracking() {
  const { trackActivity } = useActivityTracking();

  const trackDownload = useCallback((
    fileName: string,
    fileType: string,
    resourceType?: string,
    resourceId?: string
  ) => {
    trackActivity({
      activityType: 'downloaded',
      resourceType: resourceType || 'file',
      resourceId: resourceId || fileName,
      metadata: {
        file_name: fileName,
        file_type: fileType
      }
    });
  }, [trackActivity]);

  return { trackDownload };
}

/**
 * Hook for tracking tool usage
 */
export function useToolTracking() {
  const { trackActivity } = useActivityTracking();

  const trackToolUse = useCallback((
    toolName: string,
    action: string,
    metadata?: Record<string, any>
  ) => {
    trackActivity({
      activityType: 'used_tool',
      resourceType: 'tool',
      resourceId: toolName,
      metadata: {
        action,
        ...metadata
      }
    });
  }, [trackActivity]);

  return { trackToolUse };
}
